import { AppClassificationCellEditor } from './AppClassificationCellEditor';
import { AppConnectionStatusTag } from './AppConnectionStatusTag';
import { AppDescriptionCellEditor } from './AppDescriptionCellEditor';
import { AppDisplay } from '@ui/applications';
import { AppGroupCellEditor } from './AppGroupCellEditor';
import { AppLabelCellEditor } from './AppLabelCellEditor';
import { AppMonitorsCell } from './AppMonitorsCell';
import { AppOwnerCellEditor } from './AppOwnerCellEditor';
import { ApproveApplicationButton } from '@security/ApproveApplicationButton';
import { AppStakeholdersCellEditor } from './AppStakeholdersCellEditor';
import { AppUsageActivityCellEditor } from './AppUsageActivityCellEditor';
import { AppUserCellEditor } from './AppUserCellEditor';
import { ColumnType } from 'antd/es/table';
import { ConfirmDeleteButton } from '@ui/modals';
import { getClosestCardBody } from '@lib/popups';
import React from 'react';
import { When } from 'react-if';

import { COPY_SPECIFIC, MESSAGES_COPY } from '@sonar-software/sonar-ts-constants';
import {
	DataSecurityApplication,
	DataSecurityApplicationStatus,
	DataSecurityMonitorReference
} from '@sonar-software/sonar-core';
import { Space, Tag } from 'antd';

type ColumnDefinition = ColumnType<DataSecurityApplication>;

export const nameColumn: ColumnDefinition = {
	className: 'inline-flex-cell',
	dataIndex: 'name',
	fixed: 'left',
	key: 'name',
	render: (name: string, app) =>
		<div
			style={ {
				alignItems: 'center',
				display: 'flex'
			} }>
			<AppDisplay
				appId={ app.applicationId }
				hideArrows
				hideLabel />
			<When condition={ app.status === DataSecurityApplicationStatus.Blocked }>
				<Tag
					className='application-status-tag'
					color='red'>
					{ COPY_SPECIFIC.BLOCKED }
				</Tag>
			</When>
		</div>,
	title: COPY_SPECIFIC.NAME
};

const userColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'integrationUser',
	key: 'user',
	render: (user: string, app) => <AppUserCellEditor
		applicationId={ app.applicationId }
		user={ user } />,
	title: COPY_SPECIFIC.USER,
	width: '100px'
};

const statusColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'installed',
	key: 'installed',
	render: (installed: boolean) => <AppConnectionStatusTag installed={ installed } />,
	title: COPY_SPECIFIC.APPLICATION_STATUS,
	width: '140px'
};

const labelColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'label',
	key: 'label',
	render: (label: string | undefined, app) => <AppLabelCellEditor
		applicationId={ app.applicationId }
		label={ label } />,
	title: COPY_SPECIFIC.LABEL
};

const descriptionColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'description',
	key: 'description',
	render: (description: string | undefined, app) =>
		<AppDescriptionCellEditor
			applicationId={ app.applicationId }
			description={ description } />,
	title: COPY_SPECIFIC.DESCRIPTION
};

const monitorsColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'monitors',
	key: 'monitors',
	render: (monitors: DataSecurityMonitorReference[], app) =>
		<AppMonitorsCell
			applicationId={ app.applicationId }
			monitors={ monitors } />,
	title: COPY_SPECIFIC.MONITORS
};

const usageActivityColumn: ColumnDefinition = {
	align: 'center',
	className: 'no-padding-cell',
	dataIndex: 'activityPeriod',
	key: 'activityPeriod',
	render: (activityPeriod: number, app) =>
		<AppUsageActivityCellEditor
			activityPeriod={ activityPeriod }
			applicationId={ app.applicationId } />,
	title: COPY_SPECIFIC.USAGE_ACTIVITY,
	width: '132px'
};

const ownerColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'ownerId',
	key: 'owner',
	render: (_, { applicationId }) => <AppOwnerCellEditor applicationId={ applicationId } />,
	title: COPY_SPECIFIC.OWNER,
	width: '218px'
};

const stakeholdersColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'stakeholderIds',
	key: 'stakeholders',
	render: (_, { applicationId }) => <AppStakeholdersCellEditor applicationId={ applicationId } />,
	title: COPY_SPECIFIC.STAKEHOLDERS,
	width: '218px'
};

const classificationColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'classification',
	key: 'classification',
	render: (_, { applicationId }) => <AppClassificationCellEditor applicationId={ applicationId } />,
	title: COPY_SPECIFIC.CLASSIFICATION,
	width: '132px'
};

const groupColumn: ColumnDefinition = {
	className: 'no-padding-cell',
	dataIndex: 'stakeholderGroupId',
	key: 'group',
	render: (_, { applicationId }) => <AppGroupCellEditor applicationId={ applicationId } />,
	title: COPY_SPECIFIC.GROUP,
	width: '218px'
};

const hideColumn: (onHide: (app: DataSecurityApplication) => void) => ColumnDefinition = (onHide) => ({
	className: 'table-actions',
	key: 'table-actions',
	render: (_, record) =>
		<Space size='small'>
			{ record.status === DataSecurityApplicationStatus.Blocked &&
				<ApproveApplicationButton applicationId={ record.applicationId } /> }
			<ConfirmDeleteButton
				buttonId={ `delete-record-button-${record.applicationId}` }
				buttonLabel={ COPY_SPECIFIC.HIDE }
				confirmButtonProps={ {
					value: COPY_SPECIFIC.HIDE
				} }
				getPopupContainer={ getClosestCardBody }
				onConfirm={ () => onHide(record) }
				placement='bottomRight'
				title={ MESSAGES_COPY.ARE_YOU_SURE_INTEGRATION_APP }
				type='link' />
		</Space>,
	width: 100
});

export function getColumns(
	onHide: (app: DataSecurityApplication) => void
): Array<ColumnDefinition> {
	return [
		nameColumn,
		userColumn,
		statusColumn,
		labelColumn,
		descriptionColumn,
		monitorsColumn,
		usageActivityColumn,
		ownerColumn,
		stakeholdersColumn,
		classificationColumn,
		groupColumn,
		hideColumn(onHide)
	];
}
